/**
 * Tracking Health — canonical display vocabulary (frontend spec §4).
 *
 * Every map binds a wire value to its display string. The strings are
 * load-bearing (several are ratified canonical copy) — change them against
 * the spec, never ad hoc. Wire values are what the API ships verbatim.
 */

import type {
  CheckOutcome,
  ConditionReviewFlag,
  SilenceCause,
  IncidentStatus,
  LowVolumeFacts,
  ProblemId,
  ReportedState,
  RuleId,
  RuleResult,
  TdsdProvenance,
  TrackingAccessMode,
  ValidatorLayer,
} from "./types";

// ─── Hotel states (five computed + Low volume, plus the null rendering) ──────

/** Keyed by `ReportedState`, not `HotelState`: `low-volume` is never a
 *  computed state — it is either admin-declared or the derivation's own
 *  refinement of Healthy (ADR 0039) — but it is a status readers see
 *  everywhere the others are: chips, the override picker, state history. */
export const HOTEL_STATE_DISPLAY: Record<ReportedState, string> = {
  healthy: "Healthy",
  monitoring: "Monitoring",
  unhealthy: "Unhealthy",
  "not-yet-live": "Not yet live",
  "low-volume": "Low volume",
  paused: "Paused",
};

/** `reported === null` — no derivation yet. Before the seeding pass this is
 *  the entire fleet; a fact about our records, never a health verdict. */
export const UNDETERMINED_DISPLAY = "Undetermined";

// ─── Low volume: the declared category and the derived reading ───────────────
//
// One label, two ways onto it, and the copy below keeps them apart because
// they carry different consequences:
//
// - **Declared** (ADR 0026) — an operator placed the hotel in the category
//   through the override. It applied both locks until ADR 0040; the three
//   disclosures and `LOW_VOLUME_HINT` speak about THIS one; every sentence
//   that mentions a lock belongs here and nowhere else.
// - **Derived** (ADR 0039) — the daily pass read a HEALTHY hotel as having
//   fewer than the configured qualifying bookings in each of the last two
//   windows. Nothing is locked and nothing is hidden: Rules 1, 4 and 5 stand
//   down through an allowance (their FAIL is recorded `insufficient`, reason
//   `low-volume`), while Rules 2, 3 and the click floor keep watching.
//   `lowVolumeDerivedHint` speaks about this one, with the hotel's own
//   numbers.
//
// `StateChip` decides which to show from `HotelStatusFacts` (`override` vs
// `lowVolume`). Reported Low volume with no Low-volume override IS the
// derived reading whether or not its numbers came back, so that case falls to
// `LOW_VOLUME_DERIVED_HINT` rather than hedging. `LOW_VOLUME_GENERIC_HINT`,
// which claims neither path, is for a chip with no status facts AT ALL —
// state-history rows and the override picker, which cannot know.

/**
 * What the declared category means.
 *
 * It used to carry the one thing an operator had to know before choosing it:
 * that unlike every other override it applied BOTH LOCKS, taking the hotel
 * dark. **ADR 0040 (2026-09-20) withdrew that** — the category is now just a
 * label. ADR 0039's derived reading does the protecting instead, through an
 * allowance that stands Rules 1, 4 and 5 down while Rules 2, 3, the click
 * floor and the Validator keep watching, which is the same protection without
 * the hotel going unobservable and without a trusted-data-start-date reset on
 * the way out. Paused still locks; it is a different case (nothing to judge,
 * rather than too little to judge by).
 *
 * The category remains for the ultra-quiet hotels even the allowance and its
 * backstop cannot hold — see `lowVolumeDerivedHint`.
 */
export const LOW_VOLUME_EXPLANATION =
  "Genuinely low booking volume — too few bookings for the health rules' windows to say anything true about this hotel. A property that books twice a season reads Unhealthy, and that is a statement about the window, not about the tracking.";

export const LOW_VOLUME_LOCK_DISCLOSURE =
  "This is a label, not a lock. The daily rules keep evaluating this hotel and the Validator keeps sweeping it — the booking-based rules simply stand down for it, recording \u201cinsufficient\u201d instead of failing, so no rule-driven incident opens on a volume its windows cannot judge. The click floor and the tag checks keep watching, and nothing resets the trusted data start date. An incident already open stays open: the category is not a way to close one.";

/**
 * The other half, on the way out. There is no longer a consequence to warn
 * about: since ADR 0040 the category places no locks, so clearing it lifts
 * none and resets no date. The string stays (the modal still shows something
 * on the way out) and now says the true, boring thing — a reader who
 * remembers the old warning needs to be told it no longer applies.
 */
export const LOW_VOLUME_EXIT_DISCLOSURE =
  "Clearing the label is free: the category holds no locks, so nothing is lifted and the trusted data start date is untouched. The booking-based rules simply stop standing down for this hotel and judge it normally again. Any lock standing on it was placed by something else \u2014 an admin by hand, or a pause \u2014 and stays.";

/** The hover on a DECLARED Low-volume chip — the two sentences a reader needs
 *  beside the label, without opening the hotel. It mentions the locks, so it
 *  must never be shown for the derived reading, which places none. */
export const LOW_VOLUME_HINT = `${LOW_VOLUME_EXPLANATION} An admin placed this hotel here by hand; the booking rules stand down for it, and nothing is locked.`;

/** The two halves every derived-Low-volume hover shares, written once so the
 *  with-numbers and without-numbers forms cannot drift apart. */
const DERIVED_LEAD =
  "Healthy, but too few bookings for the booking rules to judge";
const DERIVED_TAIL =
  "Rules 1, 4 and 5 stand down for it; no lock is placed and nothing is hidden.";

/**
 * The hover on a DERIVED Low-volume chip where the hotel's `LowVolumeFacts`
 * are not to hand: what the daily pass concluded and — the half a reader
 * raised on the declared category will assume otherwise — that it locked
 * nothing and hid nothing. Reachable: the backend's `lowVolumeFacts()`
 * returns null when `derivedLowVolume` stands but the evidence blob is not
 * readable, which still reports `low-volume` with no override. Surfaces that
 * DO hold the facts render `lowVolumeDerivedHint` instead, which says the
 * same thing with this hotel's own counts and windows in it.
 */
export const LOW_VOLUME_DERIVED_HINT = `${DERIVED_LEAD}: fewer than the configured qualifying bookings in each of the last two windows. ${DERIVED_TAIL}`;

/** `LOW_VOLUME_DERIVED_HINT` filled from the facts: "0 and 1 qualifying
 *  bookings in the last two 14-day windows (fewer than 3)". The counts come
 *  first because they are the evidence; the threshold follows because it is
 *  a setting an operator can move, and the hover should say which value the
 *  reading was judged under. */
export function lowVolumeDerivedHint(facts: LowVolumeFacts): string {
  return `${DERIVED_LEAD}: ${facts.bookings} and ${facts.priorBookings} qualifying bookings in the last two ${facts.windowDays}-day windows (fewer than ${facts.threshold}). ${DERIVED_TAIL}`;
}

/** The hover where a chip has no facts to hand (state-history rows, the
 *  override picker) — the label's meaning without claiming which of the two
 *  paths put the hotel there, because that row cannot know. */
export const LOW_VOLUME_GENERIC_HINT =
  "Too few bookings for the booking rules' windows to judge — either the daily pass's own reading of a Healthy hotel, or a category an operator placed it in by hand. Neither locks anything: the booking rules stand down, the rest keep watching. The hotel's page says which.";

// ─── Declared access mode (backend issue 48) ─────────────────────────────────

export const ACCESS_MODE_DISPLAY: Record<TrackingAccessMode, string> = {
  programmatic: "Programmatic access",
  "ui-only": "UI-only access",
};

/** The chip. Short enough to sit beside the state chip, specific enough that
 *  nobody reads it as a compliment. */
export const UI_ONLY_ACCESS_TAG = "UI-only access";

/**
 * What the fact means for the person reading it.
 *
 * **Twice corrected, and the second correction is the one that matters.**
 *
 * It first said an `automatic` step "cannot run here", implying such steps ran
 * somewhere. On 2026-08-15 they ran nowhere — the module was read-only against
 * Google — so that was rewritten to say the buttons only record what a person
 * did, on every hotel.
 *
 * Later the same day the executor shipped (backend issue 50), and the second
 * sentence became false in a way worth being precise about: some steps ARE
 * carried out here now. The precision is per SURFACE. Google Ads is ours on
 * every hotel in the fleet, UI-only ones included — a hotel is enrolled
 * *because* it is a child of one of our MCCs — so the Ads repairs run here
 * whatever the access mode says. What a UI-only declaration moves is the
 * measurement surfaces: the container and the GA4 property are the customer's,
 * and nothing here can publish to them.
 *
 * The exposure that has never changed, through all three versions: an operator
 * who cannot make a change recording that they made it. Closing the last step
 * on a problem reports it fixed on their word, which resets the TDSD and drops
 * the hotel to Monitoring.
 */
export const UI_ONLY_ACCESS_EXPLANATION =
  "Autumn holds no programmatic access to this hotel's tag manager or analytics — access exists, but only as a seat in the customer's own console, so nothing here can publish a container, add a tag or change a GA4 setting. Its Google Ads account is unaffected: that one is ours, like every account in the fleet.";

export const UI_ONLY_ACCESS_INCIDENT_DISCLOSURE =
  "Steps on this hotel's container or analytics have to be carried out in the customer's own console, or asked of them — the buttons here only record that somebody did. Its Google Ads steps are unaffected and still run from here. Do not mark a step done until it actually is: closing the last step on a problem reports it fixed, which resets the trusted data start date.";

/** The chip's hover: the fact plus the note the declaring operator wrote,
 *  which is the half that says what to actually do. */
export function uiOnlyAccessHint(note: string | null): string {
  return note
    ? `${UI_ONLY_ACCESS_EXPLANATION} — ${note}`
    : UI_ONLY_ACCESS_EXPLANATION;
}

/** What the write surface tells an operator before they declare it. The
 *  emphasis is deliberate: this records a fact about US, and it changes
 *  nothing about the hotel — no lock, no state, no rule, no plan. */
export const ACCESS_MODE_DISCLOSURE =
  "This records what access we hold. Nothing about the hotel changes: no lock, no state, no rule, no fix plan. It exists so whoever picks up this hotel's next incident knows, before they reach for automation, that it cannot reach this hotel.";

export const ACCESS_NOTE_PROMPT =
  "Which surface, under whose login, and who at the customer to ask. The mode alone tells the next reader only what they cannot do; this is the half that tells them what they can.";

// ─── Incident statuses (nine; every close is a machine close) ────────────────

/**
 * Wire → display. Read by filters and dropdowns, and by `IncidentStatusChip`
 * for the statuses that still earn a badge.
 *
 * **Only the non-`active` half is ever chipped** (2026-08-17). `fix_applied`,
 * `verified` and the two machine closes are OUTCOMES: they say the work is done
 * and what came of it, and on a fleet row that is the reader's whole answer.
 * The five active statuses render nothing — each names a phase of OUR fix plan,
 * which is a fact about our records rather than about the hotel, and none of
 * them answers what a row is scanned for. Where the plan's phase matters it is
 * read off the steps, which carry their own `By hand` / `Automatic` tag beside
 * the button that acts on them. `IncidentStatusChip` draws that line off
 * `statusClass`, so this map stays a plain lookup.
 *
 * The wire values are untouched — the backend derives and ships all nine, and
 * they still group, sort and filter the worklist whether or not they badge.
 */
export const INCIDENT_STATUS_DISPLAY: Record<IncidentStatus, string> = {
  open: "Open",
  diagnosing: "Diagnosing",
  fix_identified: "Fix identified",
  // The wire value's own name, and never a badge: the backend flips an incident
  // here the moment its next step is `manual`, while that step is still
  // `pending`, so nobody outside has been asked yet and many manual steps are
  // ours to perform. A chip split it in two (`Needs action` / `Waiting on
  // others`) to survive that; un-chipping every active status retires the split
  // with it, and this string is once again just the status's name — correct in
  // a filter, claimed by nothing that renders.
  waiting_on_others: "Waiting on others",
  // Wire value stays research_queue; the operator label says who has it. It
  // means our own engineering is working the problem — an unsupported booking
  // engine being researched, or a rare setup handed to an engineer.
  research_queue: "With engineering",
  fix_applied: "Fix applied",
  verified: "Verified",
  closed_left_fleet: "Closed — left fleet",
  // ADR 0029: the derivation's own close for a signal that withdrew itself
  // (the rule passed on fresher data, or a stalled hotel's proof arrived).
  closed_signal_retracted: "Closed — signal retracted",
};

/**
 * The one hint on the one surviving chip. `Fix applied` is the only status a
 * reader can act on by NOT acting, and that instruction exists nowhere else on
 * a row — without it the teal chip invites exactly the second fix log it should
 * be preventing.
 */
export const FIX_APPLIED_HINT =
  "A fix is recorded and this hotel is in Monitoring while it proves itself. Nothing is owed here: only a qualifying booking clicked on or after the trusted data start date closes it, and the daily pass does that by itself the moment one lands. Do not log a second fix — if you believe the first one did not hold, re-run the Validator.";

// ─── Hand-off phase: deleted 2026-08-17, with the split it worded ─────────────
//
// `handOffPhase`, `HAND_OFF_OURS_DISPLAY` ("Needs action"), its `theirs` twin
// and `OPERATOR_HIDDEN_STATUSES` lived here until the incident status chip was
// narrowed to outcomes only. They were the machinery for deciding WHICH
// statuses earned a badge and what two of them should be called — a question
// that stops existing once `statusClass` draws the line instead.
//
// The judgment they encoded is worth keeping in view, because it is why
// `waiting_on_others` stopped badging rather than being reworded a third time:
// it is derived from the plan's next step the moment the plan is built, while
// that step is still `pending`. Nobody outside has been asked yet, and the
// catalog's `manual` kind means "not automatable", not "ask someone" — "open
// the GA4 cross-domain settings and check the list" is ours to do. Two
// roundings of that copy (`Waiting on others` → the `Needs action` / `Waiting
// on others` split, 2026-08-15) both left a badge describing our plan's
// internal phase. `MANUAL_STEP_TAG` ("By hand") tells the operator the same
// thing one level down, on the step, next to the button that acts on it, and it
// cannot go stale relative to the step it sits on.

// ─── Check outcomes (five; `not-run` is the only display ≠ wire) ─────────────

export const CHECK_OUTCOME_DISPLAY: Record<CheckOutcome, string> = {
  pass: "pass",
  finding: "finding",
  "not-checkable": "not-checkable",
  "not-applicable": "not-applicable",
  "not-run": "not run",
};

// ─── Rule results (wire `insufficient`, not `insufficient_data`) ─────────────

export const RULE_RESULT_DISPLAY: Record<RuleResult, string> = {
  pass: "Pass",
  fail: "Fail",
  insufficient: "Insufficient data",
};

/** Rules 4–5 inside the post-fix blind window — canonical UI copy. The
 *  record's own `evidence` line carries it verbatim; render the evidence
 *  rather than re-authoring the sentence. */
export const BLIND_WINDOW_COPY = "Not ready yet — covered by post-fix guards";

/** Plain-language line per rule — what it watches. Tooltip/explainer copy
 *  beside the canonical warning names, for readers who don't know the spec. */
export const RULE_PLAIN_DESCRIPTIONS: Record<RuleId, string> = {
  "1": "watches for zero qualifying bookings on the designated purchase action since the trusted data start date, on an engine where healthy tracking is proven possible",
  "2": "watches for new bookings arriving with $0 value — the purchase tag fired but its value variable was empty",
  "3": "compares GA4 paid sessions against Ads clicks — fails when the site captures under 40% of 100+ clicks",
  "4": "compares current bookings against this hotel's own prior weeks and fails on a collapse",
  "5": "compares the current conversion rate against this hotel's own prior rate and fails on a collapse",
  "6": "watches Google Ads click volume — fails when the account delivered fewer than the configured clicks in the trailing week, which is an Ads problem rather than a tracking one",
};

/** What each rule result means, one sentence each. `insufficient` is the one
 *  people misread — it is recorded honestly and never counted as a pass. */
export const RULE_RESULT_EXPLANATIONS: Record<RuleResult, string> = {
  pass: "the window's data clears the rule",
  fail: "the rule's failure condition held over the window",
  insufficient:
    "the window cannot judge — source not readable, not enough history, or (Rules 1, 4 and 5 on a Healthy low-volume hotel) too few bookings for a verdict to mean anything — never counted as a pass",
};

/**
 * The two `insufficiencyReason` values whose wire spelling misreads on its
 * own (ADR 0039), explained on hover beside the raw reason. Every other reason
 * is explained by the record's own evidence sentence, and the raw value is
 * left as it ships — these two are the exceptions because each looks like a
 * different fact: `low-volume` reads as a state (it is an allowance on a
 * verdict), and `rule-disabled` reads as a lock (it is a setting).
 */
export const INSUFFICIENCY_REASON_EXPLANATIONS: Partial<Record<string, string>> =
  {
    "low-volume":
      "would have failed, but the hotel is Healthy and inside its low-volume allowance — a statement about volume, not tracking",
    "rule-disabled": "the click floor is switched off in settings",
  };

/** A failing rule relieved by an accepted condition — the generic half; the
 *  condition's own recorded reason is the specific half. */
export const RULE_RELIEVED_EXPLANATION =
  "the rule fails, but a human accepted this as a standing condition, so the signal auto-closes instead of opening an incident";

/**
 * A rule whose warning still stands (`standingWarning`, backend issue 45).
 * `Failing` rather than `Fail`: this is a standing state only an admin ends,
 * not this morning's verdict — which is exactly the distinction the old copy
 * ("No health rule is failing right now") got wrong.
 */
export const RULE_STANDING_DISPLAY = "Failing";
export const RULE_STANDING_EXPLANATION =
  "this rule fired and nobody has resolved it — it clears only when a fix is applied and verified. Today's evaluation can read pass with nothing fixed: Rule 2 fires once per newly observed $0 booking and cannot re-fire, and a fix applied resets the trusted data start date, which blinds the windowed rules";

/** The same standing warning after `fix_applied` — no longer red, not yet
 *  cleared: the hotel is Monitoring while the fix proves itself. */
export const RULE_STANDING_VERIFYING_DISPLAY = "Awaiting verification";
export const RULE_STANDING_VERIFYING_EXPLANATION =
  "the fix is applied and the incident is verifying — the rule stays listed until a qualifying booking proves it";

/** Rule warning names (the "Creates:" line — what `RuleRow.title` ships and
 *  the UI renders; the spec's Part I headings are for prose only). Used for
 *  rules with no evaluation record yet, where no `title` arrives. */
export const RULE_WARNING_NAMES: Record<RuleId, string> = {
  "1": "No qualifying bookings",
  "2": "Booking recorded without usable value",
  "3": "Paid traffic massively undercounted",
  "4": "Booking tracking plummet",
  "5": "Conversion rate collapse",
  // ADR 0039 — the click floor. Its incident's primary issue is NOT this
  // title but Problem 17's ("Google Ads account not delivering clicks"),
  // stamped as the rule fires.
  "6": "Too few Google Ads clicks",
};

// ─── Accepted conditions ─────────────────────────────────────────────────────

/**
 * What accepting does, in the operator's words — the one disclosure the modal
 * owes, and the reason it is worded this bluntly.
 *
 * A condition is PROPHYLACTIC: it stamps future evaluations and findings in
 * its scope `mapsToAcceptedCondition`, which routes them to the auto-close
 * path instead of opening an incident. It deliberately does not touch an
 * incident that is already open — and since the false-positive close was
 * retired, nothing else will either. An operator who accepts a condition
 * mid-incident and expects the red to clear has to be told otherwise here,
 * because no later screen will.
 */
export const CONDITION_ACCEPT_DISCLOSURE =
  "Accepting a condition never disturbs an incident that is already open — the signal auto-closes on FUTURE runs, with the evidence logged. An open incident stays open until a fix verifies it; there is no human close.";

/** Conditions are never auto-retired — removal is the only exit, and the
 *  exit condition is prose a person reads, never a predicate. */
export const CONDITION_REMOVE_DISCLOSURE =
  "Removing is logged symmetrically and takes effect on the next run: the scope resumes opening incidents. Signals already auto-closed under this condition are not re-opened.";

export const CONDITION_REVIEW_FLAG_DISPLAY: Record<
  ConditionReviewFlag,
  string
> = {
  "route-change": "route or domain change",
  "re-enrollment": "left the fleet and returned",
};

/** Why each flag was raised — a condition keeps auto-closing while flagged;
 *  the flag asks for a human re-read, never a behavior change. */
export const CONDITION_REVIEW_FLAG_EXPLANATION: Record<
  ConditionReviewFlag,
  string
> = {
  "route-change":
    "a Problem 2 finding says this hotel's booking route or domain moved — precisely what can invalidate the measurement artifact a structural condition rests on",
  "re-enrollment":
    "this account left the fleet and re-enrolled — a commercial condition rests on a commercial relationship that demonstrably lapsed",
};

export const CONDITION_CONFIRM_DISCLOSURE =
  "Confirming clears the re-review flag and nothing else — the condition auto-closes its signal either way. It is the record that a human re-read it.";

// ─── Silence causes (six, all booking-side, all optional) ────────────────────

/**
 * The benign-cause picklist, re-homed onto the silence when the false-positive
 * close was retired. All six are booking-side explanations — the innocent
 * reasons a booking count looks wrong when nothing is broken — and naming one
 * is OPTIONAL, which is the point: it feeds the codification counter without
 * taxing an operator who only wants quiet.
 *
 * `accepted-condition` is deliberately absent. It existed on the close picklist
 * only to keep one particular hand-close from reading to the counter as
 * unexplained, and that close no longer exists.
 */
export const SILENCE_CAUSE_DISPLAY: Record<SilenceCause, string> = {
  "comped-booking": "comped booking",
  "cancellation-adjustment": "cancellation adjustment",
  "zero-dollar-promotional-rate": "$0 promotional rate",
  "demand-collapse": "demand collapse",
  "data-delay": "data delay",
  other: "other",
};

export const SILENCE_CAUSES: SilenceCause[] = [
  "comped-booking",
  "cancellation-adjustment",
  "zero-dollar-promotional-rate",
  "demand-collapse",
  "data-delay",
  "other",
];

// ─── Problem short-labels (17, pinned) ────────────────────────────────────────
// The API ships the catalog's full titles; matrix rows and chips render these.

export const PROBLEM_SHORT_LABELS: Record<ProblemId, string> = {
  "1": "Tracking code missing",
  "2": "Booking-engine domain change",
  "3": "GA4 missing purchases",
  "4": "Purchase value / currency",
  "5": "Cross-domain tracking",
  "6": "Conversion Linker",
  "7": "Cookie banner",
  "8": "Iframe propagation",
  "9": "GTM container config",
  "10": "GA4 stream config",
  "11": "Engine not emitting events",
  "12": "Unsupported engine",
  "13": "Ads conversion action",
  "14": "Site or flow unreachable",
  "15": "Destination ID mismatch",
  "16": "Multiple routes (retired)",
  // ADR 0039 — Rule 6's own problem. No Validator check exists for it, so on
  // a run's matrix it renders an empty row exactly as Problem 12 does.
  "17": "Ads not delivering clicks",
};

export function problemShortLabel(problemId: ProblemId): string {
  return `P${problemId} ${PROBLEM_SHORT_LABELS[problemId] ?? ""}`.trim();
}

// ─── Problem descriptions and solutions — see `problem-copy.ts` ──────────────

/**
 * They used to live here as two static maps keyed by problem id.
 *
 * They moved on 2026-08-15 because a map keyed by problem id alone can only
 * describe the FLEET: it says "the booking engine" on a page that knows the
 * engine is Cloudbeds, and it tells the operator to edit a Conversion Linker
 * in Tag Manager on a hotel Autumn provisions no container for. Both readings
 * are composed per hotel now, from facts the API already ships —
 * `problemDescription` / `problemSolution` in `./problem-copy`.
 *
 * The fleet-wide wording did not go anywhere: it is what those same functions
 * return when no engine is credited and no measurement setup is recorded.
 */

// ─── Check labels — what each Validator check asks, in English ───────────────

/**
 * One label per check id in `DEFAULT_VALIDATOR_CATALOG`, swept from the
 * catalog's own `title` fields 2026-08-13. The run record ships `checkId` and
 * nothing else, so this map is the only place a check gets a human name — it
 * is read by the pipeline diagram AND by the incident card's finding lines,
 * which is the point: an operator should never have to read `p3.purchase-
 * absence` to learn that GA4 is not receiving purchases.
 *
 * Kept honest against the catalog: `p13.attribution` was missing (its cells
 * rendered as the raw id), and `p13.designation` / `p13.designated-action`
 * were listed but exist only in backend test fixtures — the catalog has never
 * emitted either.
 */
export const CHECK_LABELS: Record<string, string> = {
  // api layer
  "p13.auto-tagging": "Auto-tagging (GCLID on the landing URL)",
  "p13.purchase-action-exists": "Purchase conversion action exists",
  "p13.purchase-action-unique": "Single enabled purchase action",
  "p13.counting-type": 'Counting type "Every"',
  "p13.zero-default-value": "$0 unset-value fallback pinned",
  "p13.attribution": "Attribution model specified",
  "p13.primary-for-goal": "Primary for goal",
  "p3.purchase-absence": "Purchase events arriving in GA4",
  "p4.ga4-purchase-params": "Purchase params (transaction, value, currency)",
  "p4.ads-conversion-values": "Values carried on Ads conversions",
  "p10.stream-config": "GA4 stream configuration",
  "p11.event-schema-peers": "Event schema vs engine peers",
  "p16.route-corroboration": "Routes corroborated by GA4 purchases",
  "p9.unpublished-changes": "Authored vs live container version",
  "p15.destination-map": "Destination IDs match",
  // payload layer
  "p9.template-diff": "Served container vs engine template",
  "p6.conversion-linker": "Conversion Linker auto-link domains",
  // probe layer
  "p14.reachability": "Site reachable",
  "p14.no-booking-route": "Booking route discovered",
  "p1.measurement-ids-live": "Measurement IDs live on the site",
  "p1.our-container-live": "Our container loads",
  // replay layer
  "p2.chain-diff": "Route chain matches the stored fingerprint",
  "p5.hop-gl": "Click identifier survives the hop",
  "p8.frame-src": "Checkout frame propagation",
  "p1.entry-page-tags": "Our tags fire where the hop lands",
  "p7.consent-detection": "Consent banner observed",
  // walk layer
  "p16.route-discovery": "Route discovery coverage",
  "p7.consent-beacon-diff": "Consent gate — beacons before vs after accept",
};

/** The check's English name, falling back to its id — a check this map has
 *  never met is named honestly rather than hidden. */
export function checkLabel(checkId: string): string {
  return CHECK_LABELS[checkId] ?? checkId;
}

/**
 * Split a finding's evidence into the measurement and the reasoning behind
 * it. Several checks append a ratification paragraph to their evidence (P4's
 * "(For the diagnosis: …)", P3's "Bare absence is sufficient evidence here,
 * deliberately (ratified)…") — the check justifying itself to an engineer.
 * Since 2026-08-15 operator surfaces render the `head` alone and never the
 * tail (it survives on hover via the raw sentence): an operator can't act on
 * a ratification, and the words read as jargon. The split is a sentence
 * boundary, so a check with no appended reasoning is unaffected.
 */
export function splitEvidence(evidence: string): {
  head: string;
  rest: string | null;
} {
  const match = /^(.{40,}?[.!?])\s+(?=[A-Z(])/s.exec(evidence);
  if (!match) return { head: evidence, rest: null };
  const rest = evidence.slice(match[0].length).trim();
  return rest ? { head: match[1], rest } : { head: evidence, rest: null };
}

// ─── Evidence humanizer — no raw IDs, operator-readable dates ────────────────

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** `2026-08-13` → `Aug 13 '26`. Pure string math — no timezone drift. */
function humanDay(iso: string): string {
  const [y, m, d] = iso.split("-");
  const month = MONTH_ABBR[Number(m) - 1];
  if (!month) return iso;
  return `${month} ${Number(d)} '${y.slice(2)}`;
}

/**
 * Evidence sentences are composed server-side and STORED on the row, so
 * historical rows keep whatever phrasing they were written with — including
 * raw GA4 property numbers, GTM container ids and ISO date windows. Operators
 * don't act on ids ("GA4" and "GTM" already say which surface), so display
 * strips them here rather than rewriting history. Dates render as
 * `Aug 13 '26`; a window as `Jul 31 '26 – Aug 13 '26`. The raw sentence
 * stays available wherever a hover title carries it.
 */
export function humanizeEvidence(evidence: string): string {
  return (
    evidence
      // Surface ids → the surface's name alone.
      .replace(/\bGA4 property \d+\b/g, "GA4")
      .replace(/\bGTM container GTM-[A-Z0-9]+\b/g, "GTM")
      .replace(/\bcontainer GTM-[A-Z0-9]+\b/g, "GTM container")
      .replace(/\bGTM-[A-Z0-9]{4,}\b/g, "GTM")
      .replace(/\bG-[A-Z0-9]{6,}\b/g, "GA4")
      .replace(/\bAW-\d{6,}\b/g, "Google Ads")
      .replace(/\bcustomer \d{10}\b/g, "the Ads account")
      // Date windows, then lone dates.
      .replace(
        /\b(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})\b/g,
        (_, from, to) => `${humanDay(from)} – ${humanDay(to)}`,
      )
      .replace(/\b\d{4}-\d{2}-\d{2}\b/g, (day) => humanDay(day))
  );
}

// ─── Validator layers (five, ordered) ────────────────────────────────────────

export const VALIDATOR_LAYER_ORDER: ValidatorLayer[] = [
  "api",
  "payload",
  "probe",
  "replay",
  "walk",
];

export const VALIDATOR_LAYER_DISPLAY: Record<ValidatorLayer, string> = {
  api: "API",
  payload: "Payload",
  probe: "Probes",
  replay: "Replay",
  walk: "Walk",
};

// ─── Locks — what each one does (click + hover explainer copy) ───────────────

export const LOCK_EXPLANATIONS: Record<"rules" | "validator", string> = {
  rules:
    "Pauses the daily health rules for this hotel: no pass/fail records are written while it stands, so no rule can fail and no rule can open an incident. Removing it resets the trusted data start date if any daily run was skipped while it stood.",
  validator:
    "Pauses Validator runs for this hotel: the weekly sweep, go-live and incident-triggered diagnostics all skip it (each skip is recorded). The daily health rules keep evaluating. Removing it lets runs resume — the trusted data start date is untouched.",
};

/** The one-line hover variant. */
export const LOCK_SHORT_HINTS: Record<"rules" | "validator", string> = {
  rules:
    "pauses the daily health rules — removal may reset the trusted data start date",
  validator: "pauses Validator runs (weekly sweep + diagnostics)",
};

// ─── TDSD provenance ──────────────────────────────────────────────────────────

export const TDSD_PROVENANCE_DISPLAY: Record<TdsdProvenance, string> = {
  "set-at-ads-start": "set at ads-start",
  "reset-at-fix-applied": "reset at fix applied",
  "reset-on-lock-removal": "reset on lock removal",
  "reset-at-resume": "reset at resume",
};

// ─── Fix steps / conditions / trackability / engine events ──────────────────

/**
 * Two step tags, and they are a PAIR (2026-08-15, backend issue 50).
 *
 * Until the executor shipped there was one tag, `By hand`, on every step the
 * operator had to perform — which was all of them, since nothing here called
 * Google. It was styled amber to pop, and that was the mistake the pair fixes.
 *
 * Amber in this dashboard means *waiting / caution* (`Chip`'s reserved tones,
 * §3.2). "Someone has to do this in a console" is neither. It is a routing
 * fact — who carries out the work — and dressing a routine instruction as a
 * warning spends the operator's attention on the wrong thing and cheapens the
 * colour where it does mean trouble.
 *
 * So the tags now say who acts, in the palette's own vocabulary:
 *
 * - `Autumn applies this` → **teal**, whose reserved meaning is already "our
 *   fix in flight". It marks the steps where a button really does the work.
 * - `By hand` → **neutral**, whose reserved meaning is "facts & gates". It no
 *   longer needs to shout: with the teal tag carrying the positive claim, the
 *   contrast between the two is what an eye picks up, not the hue of either.
 */
export const MACHINE_STEP_TAG = "Autumn applies this";
export const MANUAL_STEP_TAG = "By hand";

export const MACHINE_STEP_HINT =
  "This one is ours to carry out. Pressing the button makes the change on the account and publishes it — you will be shown exactly what it does first.";

/** The by-hand hover. It says where the work happens, which is the half an
 *  operator actually needs; the reason it is by hand comes from the server
 *  (`canExecute.reason`) and is shown under the step. */
export const MANUAL_STEP_HINT =
  "Nothing here can make this change — someone has to do it in the console, then record it.";

/**
 * Paused retired as a PLACEABLE override (ADR 0041). The option is gone from
 * the selector and this stands in its place, in the neutral register the house
 * keeps for "the work happens in another console" — never amber: the operator
 * has not done anything wrong, they are simply in the wrong place.
 *
 * It says what to do rather than what is forbidden, because there IS a route
 * and it is better than the one being closed: marking the customer as a test
 * account takes the hotel out of Tracking Health entirely, which is what
 * relabelling it Paused was always a worse approximation of.
 */
export const PAUSED_MOVED_TO_CRM_TITLE = "Pausing a hotel happens in the CRM";
export const PAUSED_MOVED_TO_CRM_BODY =
  "A pause is a fact about the customer, not about their tracking, so it is recorded where the customer is. Mark them as a test account in the CRM and this hotel drops out of Tracking Health on the next daily sync — no rules, no Validator, no alerts, no reminders. Nothing underneath is thrown away: the open incident, both locks and the trusted data start date are frozen exactly as they are, and un-marking the customer brings the hotel back the next morning with its whole history intact.";
export const PAUSED_MOVED_TO_CRM_LINK = "Open the CRM customer list";

/**
 * The banner on an excluded hotel's own page. The page stays fully readable —
 * that is the point of serving it — and every write control disables from the
 * server-supplied exclusion rather than being left to 409, the same way
 * LockActionModal reads its blocked reason, so the copy and the act cannot
 * disagree.
 */
export const EXCLUDED_BANNER_TITLE = "Not being watched";
export const EXCLUDED_FROZEN_NOTE =
  "Everything below is frozen at the moment this hotel left the sweep, so it is a record rather than a reading. Nothing here is lost and nothing can be changed while it stands.";

/** The confirmation an operator sees before a real write. Naming the account
 *  is the point: this is the one gesture in the dashboard that leaves it. */
export const EXECUTE_CONFIRM_TITLE = "Make this change on the account?";
export const EXECUTE_CONFIRM_DISCLOSURE =
  "This is carried out now, on the customer's live account, and takes effect immediately. It is recorded against your name. Nothing here proves it worked — the next Validator run is what confirms it, and the hotel still has to prove itself with a booking.";

/** What the by-hand button asks for. The note is mandatory on this route and
 *  not on the machine one, and the asymmetry is deliberate: the executor
 *  writes its own account of what it did, a person has to write theirs. */
export const RECORD_BY_HAND_PROMPT =
  "What did you change, and where? This becomes the record of the fix — the next person reads it instead of guessing from the step's title.";

/** How a finished step reports which of the two routes closed it. */
export function stepExecutionSummary(execution: {
  mode: "machine" | "operator-recorded";
  detail: string;
}): string {
  return execution.mode === "machine"
    ? `Carried out here — ${execution.detail}`
    : execution.detail;
}

export const TRACKABILITY_YES_DISPLAY = "Yes";
export const TRACKABILITY_UNPROVEN_DISPLAY = "unknown / unproven";

/**
 * The per-hotel "Unproven engine" tag (`engineUnproven` on the row): the
 * hotel reads Unhealthy and no hotel on any of its booking engines proves it
 * — Healthy, Monitoring, or Low volume over a computed Healthy (ADR 0039) —
 * so nobody has proven the engine can be tracked, and
 * this hotel's trouble may be the engine's, not its own. Deliberately NOT
 * named "first of a booking engine": a hotel needn't be first to carry it —
 * it carries it while its engine stands unproven, and loses it (with its
 * Validator rerun automatically) the moment any hotel proves the engine.
 */
export const ENGINE_UNPROVEN_TAG = "Unproven engine";
export const ENGINE_UNPROVEN_HINT =
  "No hotel on this booking engine is tracking correctly right now, so nobody has shown the engine can be tracked — this hotel may be broken because the engine is. The tag drops, and this hotel's checks rerun automatically, the moment any hotel on the engine turns healthy.";

/** All five verification event kinds (§5.5) — the two human kinds restored
 *  with backend issue 40's engine event log. */
export const ENGINE_VERIFICATION_DISPLAY: Record<string, string> = {
  "weekly-replay": "weekly replay",
  "discovery-walk": "go-live walk",
  "research-attempt": "research attempt",
  "attended-session": "attended session",
  "message-sent": "message sent",
};

/** Registry-completeness gaps (backend issue 41) — the amber tag copy on
 *  §5.5's OPEN INVESTIGATION column. */
export const ENGINE_PROFILE_GAP_DISPLAY: Record<string, string> = {
  "no-linker-domains": "no linker domains in registry",
  "no-booking-host-patterns": "no booking host patterns in registry",
  "no-purchase-event-name": "no purchase event name in registry",
};

/** Tag rendered unconditionally on every potential-finding row. */
export const POTENTIAL_FINDING_TAG = "readable only — never opens an incident";
